import {
  DepositPaymentTargetAccount,
  isDefined,
  Keys,
  Loadable,
  LoadableState,
  NbSelectItemOption,
  useDictionary,
} from '@eih-netbroker/common';
import { useAtom } from 'jotai';
import { useCallback, useMemo, useState } from 'react';
import { useLocale } from '../../../../context/NbLocalizationContext';
import { ErrorWidgetContent } from '../../../../shared/components/error-widget-content/ErrorWidgetContent';
import { FeedbackBox } from '../../../../shared/components/feedback-box/FeedbackBox';
import { NbLoader } from '../../../../shared/components/loader/Loader';
import { ModalFooter } from '../../../../shared/components/modal/ModalFooter';
import { NbSelect } from '../../../../shared/components/select/Select';
import { useDepositInternal } from '../../services/useDepositInternal';
import { CashActionStep, getTargetAccountAborterAtom } from '../../states/cashActionAtoms';
import classes from './CashDepositInternalTabContent.module.scss';
import { CashDepositParamEditorWrapper } from './CashDepositParamEditorWrapper';

export const CashDepositInternalTabContent = () => {
  const {
    selectedInternalDetails,
    refreshDepositDetails,
    currencyOptions,
    depositDetails,
    handleTargetAccountChange,
    setCashActionStep,
    setCashActionTemp,
    getTargetAccounts,
    cashActionTemp,
    targetAccounts,
    selectedInternalCurrency,
    setSelectedInternalCurrency,
    selectedTargetAccount,
  } = useDepositInternal();
  const [amount, setAmount] = useState<string>(cashActionTemp?.amount ?? '1');
  const { formatDictionaryType } = useDictionary();
  const { translation } = useLocale();
  const [getTargetAccountAborter, setGetTargetAccountAborter] = useAtom(getTargetAccountAborterAtom);

  const targetAccountOptions = useMemo<LoadableState<Array<NbSelectItemOption<DepositPaymentTargetAccount>>>>(() => {
    if (targetAccounts.isLoading) {
      return Loadable.loading;
    }

    if (targetAccounts.hasError) {
      return Loadable.failed;
    }

    if (targetAccounts.isLoaded) {
      return Loadable.loaded(
        targetAccounts.map(acc => ({
          id: acc,
          name: `${formatDictionaryType(acc.accountCategory, 'accountCategoryTypes')} - ${acc.clientId}`,
        })),
      );
    }

    return Loadable.uninitialized;
  }, [targetAccounts, formatDictionaryType]);

  const noTargetAccountsAvailable = useMemo<boolean>(
    () =>
      isDefined(selectedInternalDetails) &&
      isDefined(targetAccountOptions) &&
      targetAccountOptions.isLoaded &&
      targetAccountOptions.length === 0,
    [selectedInternalDetails, targetAccountOptions],
  );

  const handleSubmit = useCallback(() => {
    if (
      !isDefined(selectedInternalDetails) ||
      !isDefined(selectedTargetAccount) ||
      !isDefined(selectedInternalCurrency)
    ) {
      return;
    }

    setCashActionTemp({
      amount,
      targetClientId: selectedTargetAccount.clientId,
      sourceAccountNumber: selectedInternalDetails.accountNumber,
      targetAccountCategory: selectedTargetAccount.accountCategory,
      currencyCode: selectedInternalCurrency,
      sourceClientId: selectedInternalDetails.clientId,
    });
    setCashActionStep(CashActionStep.Summary);
  }, [
    amount,
    selectedInternalCurrency,
    selectedInternalDetails,
    selectedTargetAccount,
    setCashActionStep,
    setCashActionTemp,
  ]);

  const handleCurrencyChange = useCallback(
    (value: string) => {
      if (depositDetails.isLoaded) {
        // If there is an ongoing getter request, abort it
        getTargetAccountAborter?.abort();

        const abortController = new AbortController();
        setGetTargetAccountAborter(abortController);
        setAmount('1');
        setSelectedInternalCurrency(value);
        getTargetAccounts(depositDetails, value, abortController);
      }
    },
    [
      getTargetAccounts,
      setSelectedInternalCurrency,
      depositDetails,
      getTargetAccountAborter,
      setGetTargetAccountAborter,
    ],
  );

  const numberInputError = useMemo(() => {
    if (isDefined(selectedInternalDetails)) {
      const parsedAmount = parseFloat(amount);

      if (parsedAmount > selectedInternalDetails.amount) {
        return translation.cashAction.paramEditor.error.deposit.cannotExceedSourceAmount;
      }

      if (parsedAmount === 0) {
        return `${translation.cashAction.paramEditor.error.deposit.cannotBeZero} ${selectedInternalCurrency}!`;
      }
    }
  }, [amount, selectedInternalCurrency, selectedInternalDetails, translation]);

  if (depositDetails.isLoading || Loadable.isUninitialized(depositDetails)) {
    return (
      <div className={classes.content}>
        <NbLoader
          horizontalCenter
          verticalCenter
        />
      </div>
    );
  }

  if (depositDetails.hasError) {
    return (
      <div className={classes.error500Container}>
        <ErrorWidgetContent onClick={refreshDepositDetails} />
      </div>
    );
  }

  // Internal deposit unavailable
  if (depositDetails.isLoaded && depositDetails.messages.length !== 0) {
    if (depositDetails.messages.some(m => m.msg === Keys.CashAction.Errors.ATVEZETES_FORRAS_SZAMLA_NEM_VALID_IDOSZAK)) {
      return (
        <div className={classes.errorContainer}>
          <FeedbackBox
            type="error"
            title={translation.cashAction.deposit.error.cannotLoadDeposit}
            className={classes.errorFeedbackBox}
          />
        </div>
      );
    }

    // NETBANKBROKER_ATVEZETES_NINCS_FORRAS_SZAMLA should be filtered out, it is handled in CashDepositParamEditorWrapper component
    // Unknown error, show generic error page with refresh button
    if (
      depositDetails.messages.some(m => m.msg !== Keys.CashAction.Errors.NETBANKBROKER_ATVEZETES_NINCS_FORRAS_SZAMLA)
    ) {
      return (
        <div className={classes.error500Container}>
          <ErrorWidgetContent onClick={refreshDepositDetails} />
        </div>
      );
    }
  }

  return (
    <div className={classes.wrapper}>
      <div className={classes.content}>
        <div className={classes.selectorBlock}>
          <p className={classes.selectorLabel}>{translation.cashAction.deposit.internal.currencyLabel}</p>
          <NbSelect<string>
            value={selectedInternalCurrency}
            options={currencyOptions}
            onChange={event => handleCurrencyChange(event.target.value)}
            size="small"
            className={classes.currencySelect}
            error={noTargetAccountsAvailable}
            enforceErrorBordersWithoutMessage
          />
        </div>
        <CashDepositParamEditorWrapper
          amount={amount}
          numberInputError={numberInputError}
          handleAmountChange={setAmount}
          selectedTargetAccount={selectedTargetAccount}
          selectedCurrency={selectedInternalCurrency}
          handleTargetAccountChange={handleTargetAccountChange}
          targetAccountOptions={targetAccountOptions}
          sourceAccountOptions={undefined}
          handleSourceAccountChange={undefined}
          targetAccountAmount={selectedTargetAccount?.amount}
          selectedInternalDetails={selectedInternalDetails}
          noTargetAccountsAvailable={noTargetAccountsAvailable}
        />
      </div>

      <ModalFooter
        right={{
          title: translation.cashAction.deposit.internal.submit,
          action: handleSubmit,
          disabled:
            !isDefined(selectedInternalDetails) ||
            !isDefined(selectedTargetAccount) ||
            isDefined(numberInputError) ||
            amount === '',
        }}
      />
    </div>
  );
};
